import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { LANGUAGES } from '../constants/activities';
import LoginDialog from './LoginDialog';
import { LogIn, LogOut, Users, Copy } from 'lucide-react';

const Header: React.FC = () => {
  const { t, language, setLanguage } = useLanguage();
  const { isLoggedIn, logout } = useAuth();
  const location = useLocation();
  const [showPopup, setShowPopup] = useState(false);
  const [showLoginDialog, setShowLoginDialog] = useState(false);

  const navItems = [
    { path: '/', label: 'nav.home' },
    { path: '/chat', label: 'nav.chat' },
    { path: '/routine', label: 'nav.routine' }
  ];

  // 관리자 메뉴 아이템 추가
  const adminNavItems = [
    { path: '/admin', label: '관리자 대시보드' }
  ];

  // 팝업 표시 함수
  const showComingSoonPopup = () => {
    setShowPopup(true);
    setTimeout(() => {
      setShowPopup(false);
    }, 2000);
  };

  return (
    <>
      {/* 준비중 팝업 */}
      {showPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-sm mx-4 text-center">
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              {t('popup.coming.soon.title')}
            </h3>
            <p className="text-sm text-gray-600">
              {t('popup.coming.soon.description')}
            </p>
          </div>
        </div>
      )}

      <header className="bg-white shadow-sm border-b border-grey">
      <div className="container mx-auto px-3 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <Link to="/" className="text-xl font-bold text-mint">
              {t('app.title')}
            </Link>
            
            <nav className="hidden md:flex space-x-5">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                const classes = `px-2.5 py-1.5 rounded-lg transition-colors text-sm ${
                  isActive ? 'bg-mint text-white' : 'text-gray-600 hover:text-mint hover:bg-mint/10'
                }`;
                const isNavigable = item.path === '/' || item.path === '/chat' || item.path === '/routine';
                return isNavigable ? (
                  <Link key={item.path} to={item.path} className={classes}>
                    {t(item.label)}
                  </Link>
                ) : (
                  <button key={item.path} onClick={showComingSoonPopup} className={classes}>
                    {t(item.label)}
                  </button>
                );
              })}
              
              {/* 관리자 메뉴 (데스크톱) */}
              {isLoggedIn && adminNavItems.map((item) => {
                const isActive = location.pathname === item.path;
                const classes = `px-2.5 py-1.5 rounded-lg transition-colors text-sm ${
                  isActive ? 'bg-mint text-white' : 'text-gray-600 hover:text-mint hover:bg-mint/10'
                }`;
                return (
                  <Link key={item.path} to={item.path} className={classes}>
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </div>

          <div className="flex items-center space-x-3">
            {isLoggedIn ? (
              <>
                <button
                  onClick={logout}
                  className="p-2 text-gray-600 hover:text-mint hover:bg-mint/10 rounded-lg transition-colors"
                  title="로그아웃"
                >
                  <LogOut size={16} />
                </button>
                <div className="w-px h-4 bg-gray-300"></div>
              </>
            ) : (
              <button
                onClick={() => setShowLoginDialog(true)}
                className="p-2 text-gray-600 hover:text-mint hover:bg-mint/10 rounded-lg transition-colors"
                title="로그인"
              >
                <LogIn size={16} />
              </button>
            )}
            <div className="relative">
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="appearance-none bg-white border border-grey rounded-lg px-2.5 py-1.5 pr-6 text-xs focus:outline-none focus:ring-2 focus:ring-mint focus:border-transparent"
              >
                {LANGUAGES.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.flag} {lang.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        <nav className="md:hidden mt-3 flex space-x-1.5 overflow-x-auto">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            const classes = `px-2.5 py-1.5 rounded-lg text-xs whitespace-nowrap transition-colors ${
              isActive ? 'bg-mint text-white' : 'text-gray-600 hover:text-mint hover:bg-mint/10'
            }`;
            const isNavigable = item.path === '/' || item.path === '/chat';
            return isNavigable ? (
              <Link key={item.path} to={item.path} className={classes}>
                {t(item.label)}
              </Link>
            ) : (
              <button key={item.path} onClick={showComingSoonPopup} className={classes}>
                {t(item.label)}
              </button>
            );
          })}
          
          {/* 관리자 메뉴 (모바일) */}
          {isLoggedIn && adminNavItems.map((item) => {
            const isActive = location.pathname === item.path;
            const classes = `px-2.5 py-1.5 rounded-lg text-xs whitespace-nowrap transition-colors ${
              isActive ? 'bg-mint text-white' : 'text-gray-600 hover:text-mint hover:bg-mint/10'
            }`;
            return (
              <Link key={item.path} to={item.path} className={classes}>
                {item.label}
              </Link>
            );
          })}
        </nav>
      </div>
          </header>
      
      {/* 로그인 다이얼로그 */}
      <LoginDialog 
        isOpen={showLoginDialog} 
        onClose={() => setShowLoginDialog(false)} 
      />
    </>
  );
};

export default Header; 